// The session lease (spec/05 §8.2, spec/09 §6.2; F-SES-01, F-SES-23).
//
// A lease says "this session may run until T under offline policy P": the funded seconds counted from
// the moment it was issued, the plan that funds them, the warnings to fire and what to do if the link
// drops. The cloud issues one when a session starts and a new one, with a higher `seq`, every time the
// funded end moves (spec/05 §4.4) — a sale, a comp, a move — and the station keeps the newest it has
// verified. Online, the lease is how the station learns the session's new end (the kiosk's ring re-bases
// on it); offline, it is the only authority the station has (spec/05 §8).
//
// ---------------------------------------------------------------------------------------------
// The signed envelope
// ---------------------------------------------------------------------------------------------
//
// spec/05 §8.2 draws the lease as JSON with an Ed25519 `sig`, and spec/10 §6.2 puts it in the "same key
// family" as the policy bundle. It travels the way the bundle does (policy-bundle.ts): a **compact JWS**
// (EdDSA) minted by the api's `jwt` plugin over the one JWKS the station already trusts, whose payload is
// the `SessionLease` below plus `iss` and `aud` (`SESSION_LEASE_AUDIENCE`). The JWS signature is the
// spec's `sig`. No `exp`: a lease is persisted state the station must be able to act on while offline;
// `seq` is what retires an old one (a lease whose `seq` is not greater than the stored one for the same
// session is ignored, spec/05 §8.2).
//
// ---------------------------------------------------------------------------------------------
// What is not carried yet
// ---------------------------------------------------------------------------------------------
//
// `player` is null until the session knows the player's display name (spec/09 §6.2's `player`); the
// overrun rate and the grace block of spec/05 §8.2 are omitted until `continue_and_settle` and offline
// grace are enforced on the station. `policy` is the platform default until `sessions.offlinePolicy` is
// registered (spec/23 §5, behaviour-preserving).
import { z } from "../zod.js";
import { PlayerIdSchema, SessionIdSchema, StationIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { FundingBreakdownItemSchema, FundingBreakdownThenSchema, SessionPlannedSegmentSchema } from "../routes/sessions.js";
/** The `aud` a signed lease carries and the station requires: a lease is never a token, nor a bundle. */
export const SESSION_LEASE_AUDIENCE = 'station-lease';
/** The lease format version (spec/05 §8.2 `v`). */
export const SESSION_LEASE_VERSION = 1;
/** What the station does when the link drops mid-session (spec/05 §8.1). */
export const LeaseOfflinePolicySchema = z
    .enum(['lock_immediately', 'continue_to_lease_end', 'continue_and_settle'])
    .openapi('LeaseOfflinePolicy', {
    description: 'Offline behaviour mid-session (spec/05 §8.1): lock at once, play to the lease end (default), or play on and settle.',
});
/** Who is playing, for the kiosk's greeting (spec/09 §6.2 `session.start`'s `player`). */
export const LeasePlayerSchema = z
    .object({
    displayName: z.string().min(1),
    avatarUrl: z.string().nullable(),
})
    .meta({ id: 'LeasePlayer' });
/**
 * One segment of the lease's plan: the session plan's segment, named for the station's generated DTOs.
 * Rebuilt from its shape rather than `.meta()` on the route schema: that one carries openapi metadata, and
 * where zod-to-openapi is loaded twice (ESM and CJS, as in a Vite config) a `.meta()` over it registers
 * the id twice and throws.
 */
export const LeasePlanSegmentSchema = z.object(SessionPlannedSegmentSchema.shape).meta({ id: 'LeasePlanSegment' });
/** An amount on the lease: integer minor units and an ISO 4217 code, named for the station's generated DTOs. */
export const LeaseMoneySchema = z
    .object({
    amount: z.int(),
    currency: z.string().length(3),
})
    .meta({ id: 'LeaseMoney' });
/**
 * One source of the session's time on the lease — the session's `FundingBreakdownItem`, named for the station's
 * generated DTOs (rebuilt from its shape, as `LeasePlanSegment` is, with its amounts as `LeaseMoney`).
 */
export const LeaseFundingItemSchema = z
    .object({
    ...FundingBreakdownItemSchema.shape,
    kind: z.enum(['comp', 'pass', 'wallet', 'postpaid']),
    hourlyRate: LeaseMoneySchema.nullable(),
    amount: LeaseMoneySchema.nullable(),
})
    .meta({ id: 'LeaseFundingItem' });
/** What follows the funded time on the lease: grace, once, or the end (spec/05 §4.5). */
export const LeaseFundingThenSchema = z.object({ ...FundingBreakdownThenSchema.shape, kind: z.enum(['grace', 'end']) }).meta({ id: 'LeaseFundingThen' });
/**
 * "How much time do I have?" on the lease (spec/05 §4.4): the sources in the order they are used, their total and
 * what follows, as of `issuedAt`. The station hands it to the kiosk, which counts it down from the front between
 * leases (`@clavian/domain` `drainFundingBreakdown`). Optional: a station ignores what it does not know.
 */
export const LeaseFundingBreakdownSchema = z
    .object({
    items: z.array(LeaseFundingItemSchema),
    totalSeconds: z.int().min(0),
    then: LeaseFundingThenSchema,
})
    .meta({ id: 'LeaseFundingBreakdown' });
/** The lease's content — the JWS payload, less `iss`/`aud`/`iat` (spec/05 §8.2). */
export const SessionLeaseSchema = z
    .object({
    v: z.literal(SESSION_LEASE_VERSION),
    /** Increases with every lease for this session; the station ignores one that is not newer. */
    seq: z.int().min(0),
    sessionId: SessionIdSchema,
    stationId: StationIdSchema,
    playerId: PlayerIdSchema,
    player: LeasePlayerSchema.nullable(),
    /** When the cloud issued it; the funded seconds count from here (the station from when it accepted it). */
    issuedAt: IsoDateTimeSchema,
    /** Seconds of play funded from `issuedAt`. */
    fundedSeconds: z.int().min(0),
    /** A hard cap (a reservation hold) that never extends the funded time, or null for none. */
    capSeconds: z.int().min(0).nullable(),
    /** The plan that funds the time, in the order it draws down. */
    plan: z.array(LeasePlanSegmentSchema),
    policy: LeaseOfflinePolicySchema,
    /** Under `lock_immediately`, how long after the link drops the station locks. */
    lockAfterSeconds: z.int().min(0),
    /** Under `continue_and_settle`, how far past the funded end the station may run. */
    maxOverrunSeconds: z.int().min(0),
    /** When to warn, as seconds before the funded end (the station fires them itself offline, spec/05 §5). */
    warnings: z.array(z.int().min(1)),
    /** What the funded time is made of, in the order it is used, and what follows (spec/05 §4.4). */
    funding: LeaseFundingBreakdownSchema.optional(),
})
    .openapi('SessionLease', { description: 'A session lease: may run until the funded end under an offline policy (spec/05 §8.2).' });
/** The signed lease on the wire: a compact EdDSA JWS whose payload is a `SessionLease`. */
export const SignedSessionLeaseSchema = z.string().min(1).openapi('SignedSessionLease', {
    description: 'A compact JWS (EdDSA, the api JWKS) whose payload is a SessionLease plus `iss` and `aud: station-lease`.',
});
/** `session.lease` (server → station, 24 h, latest wins): a new lease for the running session (spec/09 §6.2). */
export const SessionLeasePayloadSchema = z
    .object({
    sessionId: SessionIdSchema,
    /**
     * The signed lease. Absent when the worker — which holds no signing key — says the funded end moved (a
     * top-up, a pass window opening or closing, an expiry): the station then reads a fresh one from
     * `GET /v1/station/sessions/current/lease`.
     */
    lease: SignedSessionLeaseSchema.optional(),
})
    .openapi('SessionLeasePayload');
/**
 * `session.start` (server → station, 60 s): a session started for this station somewhere other than its
 * own kiosk — at the console — so the station unlocks into `in_session` (spec/09 §6.2). Carries its first
 * lease, from which the station builds the kiosk's clock and plan (the signed copy is the only one it
 * trusts), and the player's display details when known. The player token of spec/09 §6.2 is not minted
 * yet, so it is absent.
 */
export const SessionStartPayloadSchema = z
    .object({
    sessionId: SessionIdSchema,
    lease: SignedSessionLeaseSchema,
    player: LeasePlayerSchema.nullable(),
})
    .openapi('SessionStartPayload');
/**
 * `session.grace` (server → station, 5 min): the session's funded time ran out and it entered grace (F-KIO-13,
 * spec/05 §4.5). The kiosk takes over with a countdown to `graceEndsAt` and the ways to keep playing — add time,
 * buy a pass, log out; time added in grace returns the session to normal funding (a new `session.lease`). A
 * station that reconnects mid-grace reads it from `station.state` (the session's `status: grace`).
 */
export const SessionGracePayloadSchema = z
    .object({
    sessionId: SessionIdSchema,
    /** When grace runs out and the session ends (`grace_expired`), RFC 3339 UTC. */
    graceEndsAt: IsoDateTimeSchema,
})
    .openapi('SessionGracePayload');
//# sourceMappingURL=lease.js.map